import { useEffect, useRef } from "react";
import { useTheme } from "../contexts/ThemeContext";
import "./Background3D.css";

const PARTICLES = 55;

export default function Background3D() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const smoothMouseRef = useRef({ x: 0.5, y: 0.5 });
  const { isDark } = useTheme();
  const isDarkRef = useRef(isDark);

  useEffect(() => {
    isDarkRef.current = isDark;
  }, [isDark]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    let animationId;
    let particles = [];
    let w = 0;
    let h = 0;

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * devicePixelRatio;
      canvas.height = h * devicePixelRatio;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);

      particles = Array.from({ length: PARTICLES }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        z: Math.random() * 1 + 0.2,
        r: Math.random() * 2.5 + 0.8,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        hue: Math.random() > 0.5 ? 145 : 160,
      }));
    };

    const onMove = (e) => {
      mouseRef.current = {
        x: e.clientX / w,
        y: e.clientY / h,
      };
    };

    const drawBlob = (x, y, radius, color) => {
      const g = ctx.createRadialGradient(x, y, 0, x, y, radius);
      g.addColorStop(0, color);
      g.addColorStop(1, "transparent");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    };

    const draw = (time) => {
      const mx = smoothMouseRef.current.x;
      const my = smoothMouseRef.current.y;
      const t = time * 0.001;

      smoothMouseRef.current.x += (mouseRef.current.x - smoothMouseRef.current.x) * 0.04;
      smoothMouseRef.current.y += (mouseRef.current.y - smoothMouseRef.current.y) * 0.04;

      ctx.clearRect(0, 0, w, h);

      const dark = isDarkRef.current;

      // Base gradient
      const bg = ctx.createLinearGradient(0, 0, w, h);
      if (dark) {
        bg.addColorStop(0, "#0b1120");
        bg.addColorStop(0.45, "#1e293b");
        bg.addColorStop(1, "#0f172a");
      } else {
        bg.addColorStop(0, "#f8fdf9");
        bg.addColorStop(0.5, "#ffffff");
        bg.addColorStop(1, "#f0fdf4");
      }
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // Animated mesh blobs
      const blobs = dark
        ? [
            {
              x: w * 0.75 + Math.sin(t * 0.4) * 50 + (mx - 0.5) * 60,
              y: h * 0.2 + Math.cos(t * 0.3) * 35 + (my - 0.5) * 45,
              r: Math.min(w, h) * 0.34,
              color: "rgba(34, 197, 94, 0.1)",
            },
            {
              x: w * 0.15 + Math.cos(t * 0.35) * 40 + (mx - 0.5) * -45,
              y: h * 0.65 + Math.sin(t * 0.45) * 40 + (my - 0.5) * -40,
              r: Math.min(w, h) * 0.28,
              color: "rgba(22, 163, 74, 0.08)",
            },
            {
              x: w * 0.5 + Math.sin(t * 0.25) * 55,
              y: h * 0.45 + Math.cos(t * 0.5) * 25,
              r: Math.min(w, h) * 0.22,
              color: "rgba(74, 222, 128, 0.06)",
            },
          ]
        : [
            {
              x: w * 0.75 + Math.sin(t * 0.4) * 60 + (mx - 0.5) * 80,
              y: h * 0.2 + Math.cos(t * 0.3) * 40 + (my - 0.5) * 60,
              r: Math.min(w, h) * 0.38,
              color: "rgba(22, 163, 74, 0.18)",
            },
            {
              x: w * 0.15 + Math.cos(t * 0.35) * 50 + (mx - 0.5) * -60,
              y: h * 0.65 + Math.sin(t * 0.45) * 50 + (my - 0.5) * -50,
              r: Math.min(w, h) * 0.32,
              color: "rgba(34, 197, 94, 0.14)",
            },
            {
              x: w * 0.5 + Math.sin(t * 0.25) * 70,
              y: h * 0.45 + Math.cos(t * 0.5) * 30,
              r: Math.min(w, h) * 0.25,
              color: "rgba(21, 128, 61, 0.1)",
            },
          ];

      blobs.forEach((b) => drawBlob(b.x, b.y, b.r, b.color));

      // Blueprint grid
      ctx.strokeStyle = dark
        ? "rgba(74, 222, 128, 0.05)"
        : "rgba(22, 163, 74, 0.04)";
      ctx.lineWidth = 1;
      const gridSize = 48;
      const offsetX = (mx - 0.5) * 20;
      const offsetY = (my - 0.5) * 20;

      for (let x = offsetX % gridSize; x < w; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      for (let y = offsetY % gridSize; y < h; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }

      // Particles + connections
      particles.forEach((p) => {
        p.x += p.vx * p.z;
        p.y += p.vy * p.z;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;

        const parallaxX = (mx - 0.5) * 30 * p.z;
        const parallaxY = (my - 0.5) * 30 * p.z;
        const px = p.x + parallaxX;
        const py = p.y + parallaxY;

        ctx.beginPath();
        ctx.arc(px, py, p.r * p.z, 0, Math.PI * 2);
        ctx.fillStyle = dark
          ? `hsla(${p.hue}, 55%, 55%, ${0.08 + p.z * 0.12})`
          : `hsla(${p.hue}, 70%, 60%, ${0.15 + p.z * 0.2})`;
        ctx.fill();
      });

      // Connection lines between nearby particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i];
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 140) {
            ctx.beginPath();
            ctx.moveTo(
              a.x + (mx - 0.5) * 30 * a.z,
              a.y + (my - 0.5) * 30 * a.z
            );
            ctx.lineTo(
              b.x + (mx - 0.5) * 30 * b.z,
              b.y + (my - 0.5) * 30 * b.z
            );
            ctx.strokeStyle = dark
              ? `rgba(74, 222, 128, ${0.05 * (1 - dist / 140)})`
              : `rgba(22, 163, 74, ${0.06 * (1 - dist / 140)})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }

      // Construction accent lines (subtle isometric feel)
      ctx.save();
      ctx.globalAlpha = dark ? 0.04 : 0.06;
      ctx.strokeStyle = dark ? "#4ade80" : "#15803d";
      ctx.lineWidth = 1.5;
      const cx = w * 0.85 + (mx - 0.5) * 40;
      const cy = h * 0.75 + (my - 0.5) * 30;
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.moveTo(cx, cy - i * 28);
        ctx.lineTo(cx + 60, cy - i * 28 - 30);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(cx, cy - i * 28);
        ctx.lineTo(cx - 60, cy - i * 28 - 30);
        ctx.stroke();
      }
      ctx.restore();

      animationId = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMove, { passive: true });
    animationId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  return (
    <div className="bg-3d" aria-hidden="true">
      <canvas ref={canvasRef} className="bg-3d-canvas" />
      <div className="bg-3d-vignette" />
    </div>
  );
}
